/**
 * ConfirmDialog — the promise-based replacement for window.confirm/alert/prompt.
 *
 * The CRM still calls the native dialogs in a hundred places, and every one of
 * them is a bug in a tool that runs beside a live phone call: the browser
 * dialog freezes the tab (SMS polling, the softphone, the agent tick all stop),
 * it renders in Chrome's own face outside the app's dark mode, and Chrome lets
 * a user tick "prevent this page from creating more dialogs" — after which
 * every later confirm silently returns false and destructive actions look like
 * they simply did nothing.
 *
 * Call sites keep their shape. `if (!(await confirm({...}))) return;` reads the
 * same as `if (!window.confirm(...)) return;`, so a migration is a one-line
 * edit per site and never restructures a handler.
 *
 * BINDING SEMANTICS — these are what call sites are allowed to rely on:
 * - confirm resolves `true` ONLY from the confirm button. Escape, a mousedown
 *   on the overlay itself, the cancel button, and provider unmount all resolve
 *   `false`. There is no path where an accidental dismissal reads as consent.
 * - prompt resolves the CURRENT input string from the submit button or Enter
 *   (single-line only — Enter in a textarea types a newline). Every other exit
 *   resolves `null`. `''` and `null` are DISTINCT: empty means the user
 *   submitted nothing, null means they never submitted. The value is NEVER
 *   trimmed — trimming is a call-site decision (a template body's trailing
 *   newline is not the same as a name's stray space).
 * - alert resolves void on any close path.
 * - While a dialog is open it OWNS Escape: the key is caught in the capture
 *   phase and stopped, so one press closes the dialog and nothing beneath it
 *   (several CRM modals raise a confirm from inside their own Escape-listening
 *   selves). This is what window.confirm did.
 * - Requests that arrive while a dialog is open are QUEUED and shown one at a
 *   time in arrival order. None are dropped. Each promise resolves exactly
 *   once, guarded by a per-request flag.
 * - If the provider unmounts (leaving the CRM mid-dialog), every pending and
 *   queued promise resolves with its cancel value, so no `await` is left
 *   dangling forever inside a handler that still holds a lock or a spinner.
 *
 * Deliberately NOT here: no portal (staying in the tree keeps the CRM's
 * `font-inter` scope — a portal to <body> loses it), no focus trap (the CRM's
 * existing modals do not trap either, and a half-trap fights the nested
 * softphone/call overlays), and no loading state (the caller runs its async
 * work after the await, where its own spinner already lives).
 */
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Z } from './z';
import { btnDanger, btnGhost, btnPrimary, fieldCls } from './buttons';

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive confirm: red button, and focus starts on Cancel. */
  danger?: boolean;
}

export interface AlertOptions {
  title: string;
  message?: ReactNode;
  okLabel?: string;
}

export interface PromptOptions {
  title: string;
  message?: ReactNode;
  /** Field label above the input. */
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Textarea instead of a single-line input (Enter then types a newline). */
  multiline?: boolean;
  danger?: boolean;
}

/**
 * One queued request. The `settle` functions are the resolve-once wrappers
 * built in the hooks below — calling one twice is a no-op by construction.
 */
type DialogRequest =
  | { kind: 'confirm'; opts: ConfirmOptions; settle: (value: boolean) => void }
  | { kind: 'alert'; opts: AlertOptions; settle: () => void }
  | { kind: 'prompt'; opts: PromptOptions; settle: (value: string | null) => void };

interface Pending {
  id: number;
  req: DialogRequest;
}

/** Resolve a request with the value that means "the user did not say yes". */
function cancelRequest(req: DialogRequest): void {
  switch (req.kind) {
    case 'confirm':
      req.settle(false);
      break;
    case 'alert':
      req.settle();
      break;
    case 'prompt':
      req.settle(null);
      break;
  }
}

interface DialogContextValue {
  request: (req: DialogRequest) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  // The queue lives in a ref and is mirrored to state for rendering. The ref is
  // the authority: two requests fired in the same tick would both read a stale
  // `queue` from a state closure and one would be lost.
  const queueRef = useRef<Pending[]>([]);
  const [queue, setQueue] = useState<Pending[]>([]);
  const nextId = useRef(1);

  const request = useCallback((req: DialogRequest) => {
    queueRef.current = [...queueRef.current, { id: nextId.current++, req }];
    setQueue(queueRef.current);
  }, []);

  /** Retire the dialog on screen; the next queued request takes its place. */
  const closeCurrent = useCallback(() => {
    queueRef.current = queueRef.current.slice(1);
    setQueue(queueRef.current);
  }, []);

  // Unmounting the provider (leaving the CRM) must not strand an awaiting
  // caller — everything still open or queued resolves as cancelled. Under
  // StrictMode's dev remount the queue is empty here, so this is a no-op.
  useEffect(
    () => () => {
      const stranded = queueRef.current;
      queueRef.current = [];
      stranded.forEach((p) => cancelRequest(p.req));
    },
    []
  );

  const value = useMemo(() => ({ request }), [request]);
  const current = queue[0];

  return (
    <DialogContext.Provider value={value}>
      {children}
      {/* Keyed by request id: each dialog gets a fresh instance, which is what
          resets the prompt's input and re-runs autofocus between queued items. */}
      {current && <DialogView key={current.id} req={current.req} onClose={closeCurrent} />}
    </DialogContext.Provider>
  );
}

function useDialogContext(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('CRM dialogs must be used within a <ConfirmProvider>');
  return ctx;
}

/** `await confirm({ title })` → true only if the user pressed Confirm. */
export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const { request } = useDialogContext();
  return useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        let done = false;
        request({
          kind: 'confirm',
          opts,
          settle: (value) => {
            if (done) return;
            done = true;
            resolve(value);
          },
        });
      }),
    [request]
  );
}

/** `await alert({ title })` → resolves once the user acknowledges. */
export function useAlertDialog(): (opts: AlertOptions) => Promise<void> {
  const { request } = useDialogContext();
  return useCallback(
    (opts: AlertOptions) =>
      new Promise<void>((resolve) => {
        let done = false;
        request({
          kind: 'alert',
          opts,
          settle: () => {
            if (done) return;
            done = true;
            resolve();
          },
        });
      }),
    [request]
  );
}

/** `await prompt({ title })` → the untrimmed string, or null if cancelled. */
export function usePromptDialog(): (opts: PromptOptions) => Promise<string | null> {
  const { request } = useDialogContext();
  return useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        let done = false;
        request({
          kind: 'prompt',
          opts,
          settle: (value) => {
            if (done) return;
            done = true;
            resolve(value);
          },
        });
      }),
    [request]
  );
}

/**
 * The dialog itself — ui/ConfirmModal's markup, kept local to the kit so the
 * shared modal stays a plain controlled component.
 */
function DialogView({ req, onClose }: { req: DialogRequest; onClose: () => void }) {
  const [value, setValue] = useState(() =>
    req.kind === 'prompt' ? req.opts.initialValue ?? '' : ''
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const inputId = useId();

  // `onClose` shifts the queue, so calling it twice would silently drop the
  // NEXT queued dialog without resolving it. One latch per dialog instance
  // makes that unreachable no matter how the close was triggered.
  const closedRef = useRef(false);
  const close = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose();
  }, [onClose]);

  const cancel = useCallback(() => {
    cancelRequest(req);
    close();
  }, [req, close]);

  const accept = useCallback(() => {
    switch (req.kind) {
      case 'confirm':
        req.settle(true);
        break;
      case 'alert':
        req.settle();
        break;
      case 'prompt':
        // The raw value. Trimming belongs to the call site.
        req.settle(value);
        break;
    }
    close();
  }, [req, close, value]);

  // Escape closes (as cancel) and the page behind cannot scroll while open —
  // the same pairing ui/ConfirmModal uses, with one difference that matters:
  // the listener is CAPTURE phase and stops the event. Existing CRM modals
  // listen for Escape on document too, and several of them raise a confirm from
  // inside themselves; those handlers were registered first, so a bubble-phase
  // listener here means one Escape press closes this dialog AND the modal
  // underneath it. Capture + stopPropagation gives the dialog exclusive
  // ownership of Escape while it is open, which is what window.confirm did.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      cancel();
    };
    document.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prev;
    };
  }, [cancel]);

  // Where the keyboard lands. A destructive confirm deliberately focuses
  // Cancel: Enter on a freshly-raised dialog must not delete anything.
  useEffect(() => {
    if (req.kind === 'prompt') {
      const el: HTMLInputElement | HTMLTextAreaElement | null = inputRef.current ?? textareaRef.current;
      if (el) {
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }
      return;
    }
    if (req.kind === 'confirm' && req.opts.danger) {
      cancelRef.current?.focus();
      return;
    }
    confirmRef.current?.focus();
  }, [req]);

  const { title, message } = req.opts;
  const danger = req.kind === 'alert' ? false : req.opts.danger === true;
  const confirmLabel =
    req.kind === 'alert'
      ? req.opts.okLabel ?? 'OK'
      : req.opts.confirmLabel ?? (req.kind === 'prompt' ? 'OK' : 'Confirm');

  return (
    <div
      className={`fixed inset-0 ${Z.confirm} flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center`}
      onMouseDown={(e) => {
        // Only a press on the backdrop itself — never one bubbled up from the panel.
        if (e.target === e.currentTarget) cancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="my-8 w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        {message && <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">{message}</div>}

        {req.kind === 'prompt' && (
          <div className="mt-3">
            {req.opts.label && (
              <label
                htmlFor={inputId}
                className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400"
              >
                {req.opts.label}
              </label>
            )}
            {req.opts.multiline ? (
              <textarea
                id={inputId}
                ref={textareaRef}
                rows={3}
                value={value}
                placeholder={req.opts.placeholder}
                onChange={(e) => setValue(e.target.value)}
                className={`${fieldCls} w-full`}
              />
            ) : (
              <input
                id={inputId}
                ref={inputRef}
                type="text"
                value={value}
                placeholder={req.opts.placeholder}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  // An IME composition (Japanese/Chinese candidate selection)
                  // ends with Enter — that keypress confirms the candidate, it
                  // does not submit the dialog.
                  if (e.nativeEvent.isComposing) return;
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    accept();
                  }
                }}
                className={`${fieldCls} w-full`}
              />
            )}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-3">
          {req.kind !== 'alert' && (
            <button type="button" ref={cancelRef} onClick={cancel} className={btnGhost}>
              {req.opts.cancelLabel ?? 'Cancel'}
            </button>
          )}
          <button
            type="button"
            ref={confirmRef}
            onClick={accept}
            className={danger ? btnDanger : btnPrimary}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
